<script setup>
import { ref, watch, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const results = ref([]);
const isFetching = ref(false);
const currentIndex = ref(0);
const inputRef = ref(null);

const hasResults = computed(() => results.value.length > 0);
const hasQuery = computed(() => query.value.trim().length > 0);
const totalResults = computed(() => results.value.length);
const currentResultNumber = computed(() =>
  hasResults.value ? currentIndex.value + 1 : 0
);

const scrollToCurrentResult = () => {
  const message = results.value[currentIndex.value];
  if (!message) return;
  store.dispatch('conversationSearch/setActiveConversationSearch', {
    query: query.value,
    messageId: message.id,
  });
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: message.id,
    highlight: true,
  });
};

const fetchResults = useDebounceFn(async q => {
  const search = q.trim();
  if (!search) {
    results.value = [];
    currentIndex.value = 0;
    return;
  }
  isFetching.value = true;
  results.value = await store.dispatch(
    'conversationSearch/conversationMessageSearch',
    {
      conversationId: props.conversationId,
      q: search,
    }
  );
  isFetching.value = false;
  currentIndex.value = 0;
  if (results.value.length > 0) {
    await nextTick();
    scrollToCurrentResult();
  }
}, 300);

watch(query, fetchResults);

const goNext = () => {
  if (!hasResults.value) return;
  currentIndex.value = (currentIndex.value + 1) % results.value.length;
  scrollToCurrentResult();
};

const goPrev = () => {
  if (!hasResults.value) return;
  currentIndex.value =
    (currentIndex.value - 1 + results.value.length) % results.value.length;
  scrollToCurrentResult();
};

const clear = () => {
  query.value = '';
  results.value = [];
  currentIndex.value = 0;
};

onMounted(() => {
  nextTick(() => {
    inputRef.value?.focus();
  });
});

onUnmounted(() => {
  store.dispatch('fetchLatestMessages', {
    conversationId: props.conversationId,
  });
});
</script>

<template>
  <div class="flex flex-col w-full border-b border-n-weak bg-n-surface-2 z-10">
    <div class="flex items-center gap-2 px-3 py-2">
      <Icon icon="i-lucide-search" class="text-n-slate-11 size-4 shrink-0" />
      <input
        ref="inputRef"
        v-model="query"
        type="text"
        class="flex-1 min-w-0 bg-transparent text-sm outline-none text-n-slate-12 placeholder-n-slate-10"
        :placeholder="t('CONVERSATION.SEARCH_MESSAGES_PLACEHOLDER')"
      />
      <button v-if="isFetching" class="text-n-slate-10 shrink-0" disabled>
        <Icon icon="i-lucide-loader-2" class="size-4 animate-spin" />
      </button>
      <button
        v-if="hasQuery && !isFetching"
        class="text-n-slate-10 hover:text-n-slate-12 shrink-0"
        @click="clear"
      >
        <Icon icon="i-lucide-x" class="size-4" />
      </button>
      <button
        class="text-n-slate-10 hover:text-n-slate-12 shrink-0"
        @click="emit('close')"
      >
        <Icon icon="i-lucide-x" class="size-4" />
      </button>
    </div>
    <div
      v-if="hasQuery && !isFetching && hasResults"
      class="flex items-center justify-center gap-3 px-3 py-1.5 border-t border-n-weak text-sm"
    >
      <button
        class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-30 disabled:cursor-not-allowed"
        @click="goPrev"
      >
        <Icon icon="i-lucide-chevron-up" class="size-4" />
      </button>
      <span class="text-n-slate-11 text-xs font-medium tabular-nums">
        {{ currentResultNumber }} {{ t('CONVERSATION.SEARCH_OF') }}
        {{ totalResults }}
      </span>
      <button
        class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-30 disabled:cursor-not-allowed"
        @click="goNext"
      >
        <Icon icon="i-lucide-chevron-down" class="size-4" />
      </button>
    </div>
    <div
      v-if="hasQuery && !isFetching && !hasResults"
      class="flex items-center justify-center px-3 py-1.5 border-t border-n-weak text-n-slate-11 text-xs"
    >
      {{ t('CONVERSATION.SEARCH_MESSAGES_NO_RESULTS') }}
    </div>
  </div>
</template>
